import React, { useEffect, useState } from 'react'
import { EcomDataLayerHub } from '../../javascript/analytics/ecommerceDataLayerEventHub.services'
import { getProgramsWithCompareDetails } from '../helpers/helpers'
import { IEpiProps, IEventRegistrationRefreshModel, IRenewalEligibilityResponse, ISpecialtyProgram, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext } from '../reducers/event-reg-refresh-reducers'
import { Alert } from './alert'
import { CompareToolItem } from './compare-tool-item'
import { LoadingSpinner } from './loading-spinner'


interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

interface IProps {
  programs: ISpecialtyProgram[]
}

export const CompareTool = (props: IProps) => {
  const { epiProps } = useEventRegRefreshContext()!
  const [numberOfProgramsToDisplay, setNumberOfProgramsToDisplay] = useState<number>(2)
  const [selectedProgramCodes, setSelectedProgramCodes] = useState<string[]>([])
  // The compare tool only shows if there are 3 or more programs with comparison info.
  // If there are more than three programs with comparison info, show a max of three compare dropdowns.
  // If there are 3 programs with comparison info, show a max of two compare dropdowns.

  const programsWithCompareDetails = getProgramsWithCompareDetails(props.programs);
  const maxDisplay = programsWithCompareDetails.length > 3 ? 3 : programsWithCompareDetails.length - 1;
  // Function to handle the event logic, which can be called from multiple places
  const captureProgramCompareEvent = (programCodes: string[]) => {
    const selectedPrograms = programCodes.map((code) =>
      programsWithCompareDetails.find((program) => program.programCode === code),
    )
    const validSelectedPrograms = selectedPrograms.filter((program) => program !== undefined) as ISpecialtyProgram[]
    const programNames = validSelectedPrograms.map((p) => p.programName)
    EcomDataLayerHub.viewProgramCompare(programNames)
  }
  // Initialize with first two programs when component mounts
  useEffect(() => {
    if (programsWithCompareDetails.length >= 2) {
      const initialProgramCodes = [programsWithCompareDetails[0].programCode, programsWithCompareDetails[1].programCode]
      setSelectedProgramCodes(initialProgramCodes)
      // Call the event function here for the initial state
      captureProgramCompareEvent(initialProgramCodes)
    }
  }, []);

  const renderCompareItems = () => {
    const items = [];
    for (let i = 0; i < numberOfProgramsToDisplay; i++) {
      items.push(
        <CompareToolItem
          key={i}
          programs={programsWithCompareDetails}
          selectedProgramCode={selectedProgramCodes[i]}
          unavailableProgramCodes={selectedProgramCodes} //If an item is selected in a dropdown, it should not be available in any dropdown.
          onProgramSelect={(programCode) => handleProgramSelection(i, programCode)}
        />
      );
    }
    return items;
  };

  const addProgramToDisplay = () => {
    const newCount = numberOfProgramsToDisplay + 1;
    setNumberOfProgramsToDisplay(newCount);

    setSelectedProgramCodes(currentCodes => {
      const updated = [...currentCodes];
      const newIndex = newCount - 1; // Index for the new dropdown

      if (!updated[newIndex]) {
        // Find first available program not already selected
        const availableProgram = programsWithCompareDetails.find(program =>
          !currentCodes.includes(program.programCode)
        );
        if (availableProgram) {
          updated[newIndex] = availableProgram.programCode;
        }
      }
      return updated;
    });
  }

  const resetToTwoPrograms = () => {
    const newCodes = selectedProgramCodes.slice(0, 2)
    setNumberOfProgramsToDisplay(2)
    setSelectedProgramCodes(newCodes)
    // Call the event function when resetting
    captureProgramCompareEvent(newCodes)
  }

  const handleProgramSelection = (itemIndex: number, programCode: string) => {
    setSelectedProgramCodes(prev => {
      const updated = [...prev]
      updated[itemIndex] = programCode
      captureProgramCompareEvent(updated)
      return updated
    })
  }

  const showResetToTwoProgramsCta = () => {
    //If there are only 3 programs with compare info, we show 2 dropdowns, and no need for a 'reset' CTA.
    if (programsWithCompareDetails && programsWithCompareDetails.length === 3) return false;
    else return numberOfProgramsToDisplay >= maxDisplay;
  }

  return (
    <div>
      <p dangerouslySetInnerHTML={{ __html: epiProps.compareProgramsModalDescription }}></p>
      <div className="compare-tool">
        <div className="compare-tool-row">
          {renderCompareItems()}
        </div>

        {numberOfProgramsToDisplay < maxDisplay && (
          <button
            className="btn-primary"
            onClick={() => addProgramToDisplay()}>{epiProps.compareProgramsAddProgramCtaText}</button>
        )}
        {
          showResetToTwoProgramsCta() &&
          <button
            className="btn-primary"
            onClick={() => resetToTwoPrograms()}>{epiProps.compareProgramsResetProgramsCtaText}</button>
        }
      </div>
    </div>
  )
}